import { Link } from 'react-router-dom';
import { useProjects } from '../api/queries';
import { useSession } from '../auth/session';

// Wording follows docs/implementation/project-loop-pilots.md (d8364aa). It
// states the owner's selection and current status only: no metrics, progress,
// people, schedule or public deployment are claimed here.

interface Pilot {
  id: string;
  title: string;
  identity: string;
  goal: string;
  boundary: string;
  status: string;
  repository?: { href: string; label: string };
}

export const PILOTS: Pilot[] = [
  {
    id: 'project:gitfitcode-hub',
    title: 'GitFitCode Hub',
    identity: 'This community research hub and its invited project context.',
    goal: 'Use the hub’s actual changes, review feedback and fixes to test a useful project update and next step.',
    boundary: 'Project evidence keeps its source and review status; public sharing is a separate decision.',
    status: 'Selected pilot',
  },
  {
    id: 'project:upahead-mvp',
    title: 'UpAhead MVP',
    identity: 'An existing product project, confirmed by the owner.',
    goal: 'Test the same update, feedback and next-action loop on an existing product project.',
    boundary:
      'The project is registered first; the scope of private development material is reviewed before it is included in shared updates or portfolios.',
    status: 'Selected pilot',
    repository: { href: 'https://github.com/UpAhead-Inc/mvp', label: 'UpAhead-Inc/mvp on GitHub' },
  },
];

const LOOP: [string, string][] = [
  ['Name the question or goal', 'The project owner names a question or goal, the permitted input, a draft reviewer, the intended audience and what may be kept.'],
  ['Start from one permitted signal', 'One authorized working conversation, development update or other permitted project signal. Unrelated discussion stays out of the draft.'],
  [
    'Draft the update',
    'What changed or was tried, the evidence or artifact, the unresolved problem, the help requested and the next step. Reported outcomes stay separate from checked ones.',
  ],
  ['The owner corrects and approves', 'Including who it is for. Nothing is posted automatically: the destination may be a private project thread, a member thread, or no share.'],
  ['Ask for one concrete response', 'Record whether the help was useful and whether a next action is known. No response stays unknown, not a negative result.'],
  ['Review the cycle', 'Continue, reframe or stop. Evidence supports a portfolio only with the owner’s permission, and public visibility needs separate permission.'],
];

export const operationsLink = (event: 'project_update' | 'support_requested', projectId: string) =>
  `/steward/operations?event=${event}&project=${encodeURIComponent(projectId)}`;

function Registration({ pilot }: { pilot: Pilot }) {
  const projects = useProjects();
  if (projects.isPending) return <>Checking…</>;
  if (projects.isError) return <>Could not be checked right now</>;
  const registered = projects.data.items.some((project) => project.id === pilot.id);
  return registered ? (
    <>
      Registered as a hub project ·{' '}
      <Link to={`/explore?project=${encodeURIComponent(pilot.id)}`}>
        Explore its evidence<span className="visually-hidden"> for {pilot.title}</span>
      </Link>
    </>
  ) : (
    <>Not yet registered as a hub project for your account</>
  );
}

/** Shown only where the service reports that this account manages the pilot. */
function SetupLink({ pilot }: { pilot: Pilot }) {
  const projects = useProjects();
  const manages = projects.data?.items.some((project) => project.id === pilot.id && project.canManage === true) === true;
  if (!manages) return null;
  return (
    <Link to={`/projects/${encodeURIComponent(pilot.id)}/setup`} className="text-link" aria-label={`Project setup for ${pilot.title}`}>
      Project setup →
    </Link>
  );
}

function PilotCard({ pilot }: { pilot: Pilot }) {
  const session = useSession();
  const titleId = `pilot-${pilot.id.replace(/[^a-z0-9-]/gi, '-')}`;
  return (
    <section className="pilot" aria-labelledby={titleId}>
      <div>
        <p className="tag tag--class pilot__status">{pilot.status}</p>
        <h2 id={titleId} className="pilot__title">
          {pilot.title}
        </h2>
      </div>
      <dl className="finding__facts">
        <div>
          <dt>What it is</dt>
          <dd>
            {pilot.identity}
            {pilot.repository !== undefined && (
              <>
                {' '}
                <a href={pilot.repository.href} target="_blank" rel="noreferrer noopener">
                  {pilot.repository.label}
                  <span className="visually-hidden"> (opens in a new tab)</span>
                </a>
              </>
            )}
          </dd>
        </div>
        <div>
          <dt>Goal</dt>
          <dd>{pilot.goal}</dd>
        </div>
        <div>
          <dt>Evidence boundary</dt>
          <dd>{pilot.boundary}</dd>
        </div>
        <div>
          <dt>In the hub</dt>
          <dd>
            <Registration pilot={pilot} />
          </dd>
        </div>
      </dl>
      <div>
        <p className="minor-title">Practical next step</p>
        <p className="link-list">
          <Link to={`/projects/${encodeURIComponent(pilot.id)}`} className="text-link">
            Open the {pilot.title} project page →
          </Link>
          <SetupLink pilot={pilot} />
        </p>
        {/* Reviewed updates are recorded by the owner from the project page; only support requests go through Operations. */}
        {session.hasRole('steward') ? (
          <ul className="link-list">
            <li>
              <Link
                className="button button--secondary"
                to={operationsLink('support_requested', pilot.id)}
                aria-label={`Record a support request for ${pilot.title}`}
              >
                Record a support request
              </Link>
            </li>
          </ul>
        ) : (
          <p className="small">
            The project owner records reviewed updates for {pilot.title} from its project page. If you need help, a steward can record a
            support request for you.
          </p>
        )}
      </div>
    </section>
  );
}

export function Pilots() {
  return (
    <main id="main" tabIndex={-1} className="page page--wide">
      <header className="page__header">
        <p className="eyebrow">Selected pilots · September 11, 2026</p>
        <h1 className="page__title">Two projects, one reviewed loop</h1>
        <p className="lede">
          The owner selected GitFitCode Hub and UpAhead MVP as the first two pilots of the project loop, and implementation is underway.
          This direction builds on the research report: the six research chapters and their proposed findings keep their review status.
        </p>
        <p className="link-list">
          <Link className="button button--secondary" to="/projects/new">
            Start a new project with a goal
          </Link>
        </p>
      </header>

      <section className="panel" aria-labelledby="first-question">
        <h2 id="first-question" className="panel__title">
          What the first cycle asks
        </h2>
        <p>
          Does one reviewed cycle help its project owner report less, get useful help, and keep an accurate project history? It is not
          about reaching a posting cadence, a revenue model or a retention target.
        </p>
        <p className="small muted">
          No numeric target, schedule, contributor assignment, revenue result, delivery claim or posting permission is set by this plan.
          Success is shown by a useful cycle with participants, not by this page.
        </p>
      </section>

      <div className="pilot-grid">
        {PILOTS.map((pilot) => (
          <PilotCard key={pilot.id} pilot={pilot} />
        ))}
      </div>

      <section className="finding__section" aria-labelledby="loop-title">
        <h2 id="loop-title" className="section-title">
          The project loop, in order
        </h2>
        <p className="section-lede">An ordered loop, not a calendar or cadence commitment. The pilots share the loop, not identities, sources, audiences or results.</p>
        <ol className="loop-steps" aria-label="The project loop, in order">
          {LOOP.map(([title, detail]) => (
            <li key={title}>
              <strong>{title}</strong>
              {detail}
            </li>
          ))}
        </ol>
      </section>

      <section className="finding__section" aria-labelledby="limits-title">
        <h2 id="limits-title" className="section-title">
          Limits that apply to both pilots
        </h2>
        <ul className="point-list point-list--limits">
          <li>Initial drafts stay within this invited hub, and existing source-access rules continue to apply.</li>
          <li>External messages, public portfolios and any disclosure of private code need their own authorization.</li>
          <li>A draft, a source link, a response or past activity is not portfolio permission, authorship proof, an outcome or a delivery.</li>
          <li>No contributor, reviewer, steward, participant or beneficiary is assigned by this plan. A person may follow a project without starting one.</li>
        </ul>
        <p className="link-list">
          <Link to="/" className="text-link">
            Read the research report →
          </Link>
          <Link to="/participate" className="text-link">
            Ways to participate →
          </Link>
        </p>
      </section>
    </main>
  );
}
